import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  Avatar,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetTimeClockReport } from '~/thunks/report/reportThunk';
import { handleApiError } from '~/utils/helper';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect, LabelTimeHour } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';
import { handleGetJobsSelection } from '~/thunks/job/jobThunk';
import { ITimeClockReportItem } from '~/utils/interface/report';

const TimeClockReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const [startDate, setStartDate] = useState<string>(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [selectedAccounts, setSelectedAccounts] = useState<Array<string>>(['all']);
  const [selectedJob, setSelectedJob] = useState<string>('all');
  const [reportData, setReportData] = useState<Array<ITimeClockReportItem>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { selectionAccounts } = useAppSelector((state) => state.account);
  const accountOptions = useMemo(() => [{ id: 'all', name: 'All' }, ...selectionAccounts], [selectionAccounts]);

  const [jobOptions, setJobOptions] = useState<Array<any>>([{ id: 'all', name: 'All'  }]);

  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'accountAvatar', label: t('properties.accountAvatar') },
      { id: 'accountId', label: t('properties.accountId') },
      { id: 'accountName', label: t('properties.accountName') },
      { id: 'workTime', label: t('properties.workTime') },
      { id: 'breakTime', label: t('properties.breakTime') },
      { id: 'totalTime', label: t('properties.totalTime') },
    ],
    [t]
  );

  const handleSearch = () => {
    const params: any = {
      startDate,
      endDate,
    };
    if (!selectedAccounts.includes('all')) {
      params.accountId = selectedAccounts;
    }
    if (selectedJob !== 'all') {
      params.jobId = selectedJob;
    }

    dispatch(handleGetTimeClockReport(params))
      .unwrap()
      .then((response) => {
        setReportData(response.responses || []);
        setTotalCount(response.count || 0);
      })
      .catch(handleApiError);
  };

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}));
    dispatch(handleGetJobsSelection())
      .unwrap()
      .then((res) => {
        setJobOptions([{ id: 'all', name: 'All' }, ...res.responses]);
      });
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.timeClock')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-full'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.assignees')}</InputLabel>
          <BaseSelect
            options={accountOptions}
            value={selectedAccounts}
            onChange={(event) => {
              const selectedValue = event.target.value;
              if (selectedValue.includes('all') && !selectedAccounts.includes('all')) {
                setSelectedAccounts(['all']);
              } else if (selectedValue !== 'all' && selectedAccounts.includes('all')) {
                setSelectedAccounts(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccounts(selectedValue);
              }
            }}
            label={t('properties.assignees')}
            placeholder={t('properties.assignees')}
            multiple
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.job')}</InputLabel>
          <BaseSelect
            options={jobOptions}
            value={selectedJob}
            onChange={(event) => setSelectedJob(event.target.value)}
            label={t('properties.job')}
            placeholder={t('properties.job')}
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[600px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='bg-[#F0F7FF] text-[#758CA5] text-sm font-bold'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Avatar src={row.accountAvatar || ''} alt={row.accountName} />
                </TableCell>
                <TableCell>{row.accountId}</TableCell>
                <TableCell>{row.accountName}</TableCell>
                <TableCell>
                  <LabelTimeHour time={row.workTime} />
                </TableCell>
                <TableCell>
                  <LabelTimeHour time={row.breakTime} />
                </TableCell>
                <TableCell>
                  <LabelTimeHour time={row.totalTime} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TimeClockReport;
